import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  CreditCard,
  Edit3,
  Menu,
  Plus,
  ReceiptText,
  Search,
  Trash2,
  WalletCards,
  X,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import { API_BASE_URL } from "../../config/api";
const CATEGORY_OPTIONS = [
  "Internet",
  "Ujë",
  "Drita",
  "Qira",
  "Pastrim",
  "Aromatizues",
  "Materiale",
  "Mirëmbajtje",
  "Transport",
  "Furnizime",
  "Furnizim",
  "Ushqim",
  "Pije",
  "Të tjera",
];
const PAYMENT_OPTIONS = [
  "Cash",
  "Kartë",
  "Bankë",
  "Tjetër",
];
const getToday = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60000)
    .toISOString()
    .slice(0, 10);
};
const getMonthStart = () => {
  const now = new Date();
  const local = new Date(
    now.getFullYear(),
    now.getMonth(),
    1
  );
  const offset = local.getTimezoneOffset();
  return new Date(local.getTime() - offset * 60000)
    .toISOString()
    .slice(0, 10);
};
const EMPTY_FORM = {
  id: null,
  expenseDate: getToday(),
  category: "Internet",
  description: "",
  amount: "",
  paymentMethod: "Cash",
  notes: "",
};
const formatMoney = (value) =>
  new Intl.NumberFormat("sq-AL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
const formatDate = (value) => {
  if (!value) return "—";
  const datePart = String(value).slice(0, 10);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) {
    return value;
  }
  return `${day}/${month}/${year}`;
};
const getExpenseCreatorName = (expense) =>
  expense?.created_by_name ||
  expense?.createdByName ||
  expense?.user_name ||
  expense?.username ||
  expense?.created_by_username ||
  expense?.createdByUsername ||
  expense?.creator_name ||
  expense?.creatorName ||
  expense?.created_by_user_name ||
  expense?.createdByUserName ||
  (expense?.created_by ? `User #${expense.created_by}` : "—");
const isSupplierExpense = (expense) =>
  expense?.supplier_payment_id !== null &&
  expense?.supplier_payment_id !== undefined;

const AdminExpensesManager = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [dateMode, setDateMode] = useState("day");
  const [selectedDay, setSelectedDay] = useState(getToday());
  const [dateFrom, setDateFrom] = useState(getToday());
  const [dateTo, setDateTo] = useState(getToday());
  const effectiveFrom = dateMode === "day" ? selectedDay : dateFrom;
  const effectiveTo = dateMode === "day" ? selectedDay : dateTo;
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    type: "",
    text: "",
  });
const token = localStorage.getItem("authToken");
  const fetchExpenses = useCallback(async () => {
    if (!token) {
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (effectiveFrom) params.set("from", effectiveFrom);
      if (effectiveTo) params.set("to", effectiveTo);
      if (categoryFilter !== "all") {
        params.set("category", categoryFilter);
      }
      const response = await fetch(
        `${API_BASE_URL}/api/expenses?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.details ||
            data?.error ||
            data?.message ||
            "Shpenzimet nuk mund të ngarkoheshin."
        );
      }
      setExpenses(
        Array.isArray(data)
          ? data
          : data.expenses || data.data || []
      );
    } catch (error) {
      console.error("FETCH EXPENSES ERROR:", error);
      setMessage({
        type: "error",
        text:
          error?.message ||
          "Shpenzimet nuk mund të ngarkoheshin.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    token,
    navigate,
    effectiveFrom,
    effectiveTo,
    categoryFilter,
  ]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchExpenses();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [fetchExpenses]);
  const filteredExpenses = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("sq-AL");
    if (!query) {
      return expenses;
    }
    return expenses.filter((expense) => {
      const creatorName = getExpenseCreatorName(expense);
      const searchableText = [
        expense?.category,
        expense?.description,
        expense?.payment_method,
        expense?.notes,
        creatorName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("sq-AL");
      return searchableText.includes(query);
    });
  }, [expenses, search]);
  const totalExpenses = useMemo(
    () =>
      filteredExpenses.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
      ),
    [filteredExpenses]
  );
  const biggestCategory = useMemo(() => {
    const totals = {};
    filteredExpenses.forEach((expense) => {
      const category = expense.category || "Të tjera";
      totals[category] =
        (totals[category] || 0) +
        Number(expense.amount || 0);
    });
    const entries = Object.entries(totals);
    if (!entries.length) {
      return {
        category: "—",
        total: 0,
      };
    }
    entries.sort((a, b) => b[1] - a[1]);
    return {
      category: entries[0][0],
      total: entries[0][1],
    };
  }, [filteredExpenses]);
  const openNewExpense = () => {
    setFormData({
      ...EMPTY_FORM,
      expenseDate: getToday(),
    });
    setMessage({
      type: "",
      text: "",
    });
    setFormOpen(true);
  };
  const openEditExpense = (expense) => {
    if (isSupplierExpense(expense)) return;
    setFormData({
      id: expense.id,
      expenseDate: String(expense.expense_date || "").slice(0, 10),
      category: expense.category || "Të tjera",
      description: expense.description || "",
      amount: expense.amount ?? "",
      paymentMethod: expense.payment_method || "Cash",
      notes: expense.notes || "",
    });
    setMessage({
      type: "",
      text: "",
    });
    setFormOpen(true);
  };
  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setFormData(EMPTY_FORM);
  };
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };
  const saveExpense = async (event) => {
    event.preventDefault();
    if (!token) {
      navigate("/login");
      return;
    }
    if (!formData.expenseDate) {
      setMessage({
        type: "error",
        text: "Zgjidh datën e shpenzimit.",
      });
      return;
    }
    if (!formData.category.trim()) {
      setMessage({
        type: "error",
        text: "Zgjidh kategorinë.",
      });
      return;
    }
    if (!formData.description.trim()) {
      setMessage({
        type: "error",
        text: "Vendos përshkrimin.",
      });
      return;
    }
    const amount = Number(formData.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setMessage({
        type: "error",
        text: "Shuma duhet të jetë më e madhe se 0.",
      });
      return;
    }
    const editing = Boolean(formData.id);
    const endpoint = editing
      ? `${API_BASE_URL}/api/expenses/${formData.id}`
      : `${API_BASE_URL}/api/expenses`;
    setSaving(true);
    setMessage({
      type: "",
      text: "",
    });
    try {
      const response = await fetch(endpoint, {
        method: editing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          expenseDate: formData.expenseDate,
          category: formData.category.trim(),
          description: formData.description.trim(),
          amount,
          paymentMethod: formData.paymentMethod.trim(),
          notes: formData.notes.trim(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.details ||
            data?.error ||
            data?.message ||
            "Shpenzimi nuk mund të ruhej."
        );
      }
      setFormOpen(false);
      setFormData(EMPTY_FORM);
      setMessage({
        type: "success",
        text: editing
          ? "Shpenzimi u përditësua me sukses."
          : "Shpenzimi u regjistrua me sukses.",
      });
      await fetchExpenses();
    } catch (error) {
      console.error("SAVE EXPENSE ERROR:", error);
      setMessage({
        type: "error",
        text:
          error?.message ||
          "Shpenzimi nuk mund të ruhej.",
      });
    } finally {
      setSaving(false);
    }
  };
  const removeExpense = async (expense) => {
    if (isSupplierExpense(expense)) return;
    if (
      !window.confirm(
        `Ta fshijmë shpenzimin "${expense.description}"?`
      )
    ) {
      return;
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/expenses/${expense.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.details ||
            data?.error ||
            data?.message ||
            "Shpenzimi nuk mund të fshihej."
        );
      }
      setMessage({
        type: "success",
        text: "Shpenzimi u fshi me sukses.",
      });
      await fetchExpenses();
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error?.message ||
          "Shpenzimi nuk mund të fshihej.",
      });
    }
  };
  const resetFilters = () => {
    setDateMode("day");
    setSelectedDay(getToday());
    setDateFrom(getToday());
    setDateTo(getToday());
    setSearch("");
    setCategoryFilter("all");
  };
  return (
    <div className="ex-design min-h-screen bg-gray-100 text-gray-800">
      <style>{expensesDesign}</style>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      {/* MOBILE MENU BUTTON */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="fixed left-4 top-4 z-40 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-700 text-white shadow-lg transition hover:bg-blue-800 active:scale-95 lg:hidden"
        aria-label="Hap menunë"
      >
        <Menu size={24} />
      </button>
      <main className="ex-content min-h-screen px-3 pb-10 pt-20 lg:ml-64 lg:w-[calc(100%-16rem)] lg:p-8">
        {/* HEADER */}
        <div className="ex-title mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Financat e dyqanit
            </span>
            <h1 className="mt-1 text-3xl font-bold text-gray-900">
              Shpenzimet
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Kontrollo shpenzimet manuale dhe likuidimet automatike të furnitorëve.
            </p>
          </div>
          <button
            type="button"
            onClick={openNewExpense}
            className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow-md transition hover:bg-blue-700"
          >
            <Plus size={18} />
            Shto shpenzim
          </button>
        </div>
        {/* MESSAGE */}
        {message.text && (
          <div
            className={`mb-5 rounded-xl border px-4 py-3 text-sm font-medium ${
              message.type === "success"
                ? "border-green-200 bg-green-50 text-green-700"
                : "border-red-200 bg-red-50 text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}
        {/* FILTERS */}
        <section className="ex-filters mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-800">
            <CalendarDays size={18} className="text-blue-600" />
            Filtri kohor
          </div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setDateMode("day")}
              className={`rounded-lg px-4 py-2 text-sm font-semibold ${dateMode === "day" ? "bg-blue-600 text-white" : "border border-gray-200 bg-white text-gray-700"}`}>
              Ditore
            </button>
            <button type="button" onClick={() => { setDateMode("range"); setDateFrom(selectedDay); setDateTo(selectedDay); }}
              className={`rounded-lg px-4 py-2 text-sm font-semibold ${dateMode === "range" ? "bg-blue-600 text-white" : "border border-gray-200 bg-white text-gray-700"}`}>
              Periudhë kohore
            </button>
            <button type="button" onClick={() => { setDateMode("day"); setSelectedDay(getToday()); }}
              className="rounded-lg border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-700">
              Sot
            </button>
          </div>
          {dateMode === "day" && (
            <label className="mb-4 flex max-w-xs flex-col gap-1 text-xs font-semibold text-gray-600">
              Zgjidh datën
              <input type="date" value={selectedDay} max={getToday()}
                onChange={(event) => setSelectedDay(event.target.value)}
                className="h-[42px] rounded-xl border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-800" />
            </label>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            {dateMode === "range" && (<>
            <div className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
              <span>Nga</span>
              <label
                className="
                  relative flex h-[42px] cursor-pointer items-center
                  rounded-xl border border-gray-300 bg-white
                  px-3 text-sm font-semibold text-gray-800
                  transition hover:border-blue-300
                  focus-within:border-blue-500
                  focus-within:ring-4 focus-within:ring-blue-100
                "
              >
                <span>{formatDate(dateFrom)}</span>
                <CalendarDays
                  size={17}
                  className="ml-auto text-blue-600"
                />
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo || getToday()}
                  onChange={(event) => setDateFrom(event.target.value)}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  aria-label="Data nga"
                />
              </label>
              <span className="text-[10px] font-medium text-gray-400">
                ditë / muaj / vit
              </span>
            </div>
            <div className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
              <span>Deri</span>
              <label
                className="
                  relative flex h-[42px] cursor-pointer items-center
                  rounded-xl border border-gray-300 bg-white
                  px-3 text-sm font-semibold text-gray-800
                  transition hover:border-blue-300
                  focus-within:border-blue-500
                  focus-within:ring-4 focus-within:ring-blue-100
                "
              >
                <span>{formatDate(dateTo)}</span>
                <CalendarDays
                  size={17}
                  className="ml-auto text-blue-600"
                />
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  max={getToday()}
                  onChange={(event) => setDateTo(event.target.value)}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  aria-label="Data deri"
                />
              </label>
              <span className="text-[10px] font-medium text-gray-400">
                ditë / muaj / vit
              </span>
            </div>
            </>)}
            <label className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
              Kategoria
              <select
                value={categoryFilter}
                onChange={(event) =>
                  setCategoryFilter(event.target.value)
                }
                className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500"
              >
                <option value="all">Të gjitha kategoritë</option>
                {CATEGORY_OPTIONS.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>
            <label className="relative flex flex-col gap-1 text-xs font-semibold text-gray-600">
              Kërko
              <div className="relative">
                <Search
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Kerko sipas cdo te dhene "
                  className="w-full rounded-xl border border-gray-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500"
                />
              </div>
            </label>
    <div className="flex items-start">
              <button
                type="button"
                onClick={resetFilters}
                className="
                  mt-[22px]
                  ml-5
                  rounded-lg
                  bg-blue-600
                  px-4 py-2
                  text-sm font-bold
                  text-white
                  shadow-sm
                  transition
                  hover:bg-blue-700
                  active:scale-95
                "
              >
                Reset filtro
              </button>
            </div>
          </div>
        </section>
        {/* STATS */}
        <section className="ex-stats mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div className="rounded-xl bg-blue-50 p-2.5 text-blue-600">
                <WalletCards size={22} />
              </div>
              <span className="text-xs font-bold uppercase tracking-wide text-gray-400">
                Periudha
              </span>
            </div>
            <div className="text-sm font-medium text-gray-500">
              Totali i shpenzimeve
            </div>
            <div className="mt-1 text-3xl font-black text-gray-900">
              {formatMoney(totalExpenses)} ALL
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div className="rounded-xl bg-gray-100 p-2.5 text-gray-700">
                <ReceiptText size={22} />
              </div>
              <span className="text-xs font-bold uppercase tracking-wide text-gray-400">
                Regjistrime
              </span>
            </div>
            <div className="text-sm font-medium text-gray-500">
              Numri i shpenzimeve
            </div>
            <div className="mt-1 text-3xl font-black text-gray-900">
              {filteredExpenses.length}
            </div>
          </div>
          <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm sm:col-span-2 xl:col-span-1">
            <div className="mb-3 flex items-center justify-between">
              <div className="rounded-xl bg-amber-50 p-2.5 text-amber-600">
                <CreditCard size={22} />
              </div>
              <span className="text-xs font-bold uppercase tracking-wide text-gray-400">
                Më e larta
              </span>
            </div>
            <div className="text-sm font-medium text-gray-500">
              Kategoria kryesore
            </div>
            <div className="mt-1 text-xl font-black text-gray-900">
              {biggestCategory.category}
            </div>
            <div className="mt-1 text-sm font-bold text-amber-600">
              {formatMoney(biggestCategory.total)} ALL
            </div>
          </div>
        </section>
        {/* EXPENSES LIST */}
        <section className="ex-table-panel overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* MOBILE CARDS */}
          <div className="divide-y divide-gray-100 md:hidden">
            {loading ? (
              <div className="px-4 py-10 text-center text-sm text-gray-500">
                Duke ngarkuar...
              </div>
            ) : filteredExpenses.length ? (
              filteredExpenses.map((expense) => (
                <div
                  key={expense.id}
                  className="space-y-3 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-gray-400">
                        {formatDate(expense.expense_date)}
                      </div>
                      <div className="mt-1 break-words text-sm font-bold text-gray-900">
                        {expense.description}
                      </div>
                    </div>
                    <div className="shrink-0 text-right text-sm font-black text-red-600">
                      {formatMoney(expense.amount)} ALL
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-bold text-gray-700">
                      {expense.category}
                    </span>
                    {isSupplierExpense(expense) && (
                      <span className="inline-flex rounded-full bg-violet-50 px-2.5 py-1 text-[11px] font-bold text-violet-700">
                        Automatik · Furnitor
                      </span>
                    )}
                    <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700">
                      {getExpenseCreatorName(expense)}
                    </span>
                    <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                      {expense.payment_method || "—"}
                    </span>
                  </div>
                  {expense.notes ? (
                    <div className="rounded-xl bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-500">
                      {expense.notes}
                    </div>
                  ) : null}
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => openEditExpense(expense)}
                      disabled={isSupplierExpense(expense)}
                      className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-35"
                      title={isSupplierExpense(expense) ? "Pagesë furnitori: nuk modifikohet këtu" : "Modifiko"}
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeExpense(expense)}
                      disabled={isSupplierExpense(expense)}
                      className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-35"
                      title={isSupplierExpense(expense) ? "Pagesë furnitori: nuk fshihet këtu" : "Fshi"}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-10 text-center text-sm text-gray-500">
                Nuk ka shpenzime për periudhën e zgjedhur.
              </div>
            )}
            {!!filteredExpenses.length && (
              <div className="flex items-center justify-between bg-gray-50 px-4 py-3">
                <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                  Totali
                </span>
                <span className="text-base font-black text-red-600">
                  {formatMoney(totalExpenses)} ALL
                </span>
              </div>
            )}
          </div>
          {/* DESKTOP TABLE */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[780px] table-fixed text-xs">
             <colgroup>
              <col className="w-[90px]" />   {/* Data */}
              <col className="w-[100px]" />  {/* Kategoria */}
              <col className="w-[230px]" />  {/* Përshkrimi */}
              <col className="w-[125px]" />  {/* Regjistruar nga */}
              <col className="w-[90px]" />   {/* Pagesa */}
              <col className="w-[115px]" />  {/* Shuma */}
              <col className="w-[80px]" />   {/* Veprime */}
            </colgroup>
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="px-2 py-2 text-left">Data</th>
                  <th className="px-2 py-2 text-left">Kategoria</th>
                  <th className="px-2 py-2 text-left">Përshkrimi</th>
                  <th className="px-1 py-2 text-center">Regjistruar nga</th>
                  <th className="px-2 py-2 text-left">Pagesa</th>
                  <th className="px-2 py-2 text-right">Shuma</th>
                  <th className="px-2 py-2 text-center">Veprime</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-3 py-10 text-center text-gray-500"
                    >
                      Duke ngarkuar...
                    </td>
                  </tr>
                ) : filteredExpenses.length ? (
                  filteredExpenses.map((expense) => (
                    <tr
                      key={expense.id}
                      className="border-b border-gray-100 transition hover:bg-blue-50/40"
                    >
                      <td className="whitespace-nowrap px-2 py-2 font-medium">
                        {formatDate(expense.expense_date)}
                      </td>
                      <td className="px-2 py-2">
                        <span className="inline-flex rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-bold text-gray-700">
                          {expense.category}
                        </span>
                        {isSupplierExpense(expense) && (
                          <span className="mt-1 block w-fit rounded-md bg-violet-50 px-1.5 py-0.5 text-[11px] font-bold text-violet-700">
                            Automatik
                          </span>
                        )}
                      </td>
                      <td
                        className="truncate px-2 py-2 font-semibold text-gray-900"
                        title={expense.description || ""}
                      >
                        {expense.description}
                      </td>
                      <td className="px-1 py-2 text-center">
                            <span className="inline-flex rounded-md bg-blue-50 px-1.5 py-0.5 text-[11px] font-bold text-blue-700">
                              {getExpenseCreatorName(expense)}
                            </span>
                          </td>
                      <td className="truncate px-2 py-2">
                        {expense.payment_method || "—"}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right font-black text-red-600">
                        {formatMoney(expense.amount)} ALL
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEditExpense(expense)}
                      disabled={isSupplierExpense(expense)}
                            className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-50 text-blue-600 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-35"
                            title={isSupplierExpense(expense) ? "Pagesë furnitori: nuk modifikohet këtu" : "Modifiko"}
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeExpense(expense)}
                      disabled={isSupplierExpense(expense)}
                            className="flex h-7 w-7 items-center justify-center rounded-md bg-red-50 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-35"
                            title={isSupplierExpense(expense) ? "Pagesë furnitori: nuk fshihet këtu" : "Fshi"}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-3 py-10 text-center text-gray-500"
                    >
                      Nuk ka shpenzime për periudhën e zgjedhur.
                    </td>
                  </tr>
                )}
              </tbody>
              {!!filteredExpenses.length && (
                <tfoot>
                  <tr className="bg-gray-50">
                    <td
                      colSpan="5"
                      className="px-2 py-2 text-right text-[11px] font-bold uppercase tracking-wide text-gray-500"
                    >
                      Totali
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-right text-sm font-black text-red-600">
                      {formatMoney(totalExpenses)} ALL
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      </main>
      {/* MODAL */}
      {formOpen && (
        <div className="ex-modal fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[94vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {formData.id
                    ? "Modifiko shpenzimin"
                    : "Shto shpenzim"}
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  Të dhënat vendosen manualisht.
                </p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            <form
              onSubmit={saveExpense}
              className="space-y-4 p-5"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
                  Data *
                  <input
                    type="date"
                    name="expenseDate"
                    value={formData.expenseDate}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:border-blue-500"
                    required
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
                  Kategoria *
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 outline-none focus:border-blue-500"
                    required
                  >
                    {CATEGORY_OPTIONS.map((category) => (
                      <option
                        key={category}
                        value={category}
                      >
                        {category}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
                Përshkrimi *
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="P.sh. Fatura mujore e internetit"
                  className="rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:border-blue-500"
                  required
                />
              </label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
                  Shuma (ALL) *
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="0"
                    className="rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:border-blue-500"
                    required
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
                  Mënyra e pagesës
                  <select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 outline-none focus:border-blue-500"
                  >
                    {PAYMENT_OPTIONS.map((payment) => (
                      <option
                        key={payment}
                        value={payment}
                      >
                        {payment}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
                Shënime
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Shënime opsionale..."
                  className="resize-none rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:border-blue-500"
                />
              </label>
              <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="rounded-xl border border-gray-300 px-5 py-2.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Anulo
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-blue-600 px-5 py-2.5 font-semibold text-white hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50"
                >
                  {saving
                    ? "Duke ruajtur..."
                    : formData.id
                      ? "Ruaj ndryshimet"
                      : "Regjistro shpenzimin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminExpensesManager;
const expensesDesign = `
/* Shpenzime — vetëm designi, sipas Furnitorëve dhe Klientëve. */
.ex-design{background:#f9fafb;color:#111827;font-family:inherit}.ex-content,.ex-modal{font-family:inherit;font-size:14px;font-weight:400;line-height:1.5;color:#374151}.ex-content button,.ex-content input,.ex-content select,.ex-modal button,.ex-modal input,.ex-modal select,.ex-modal textarea{font-family:inherit;font-size:14px;letter-spacing:normal;line-height:1.5}.ex-content .font-black,.ex-content .font-extrabold{font-weight:700}.ex-content .font-bold{font-weight:600}.ex-content .shadow-sm{box-shadow:none}
.ex-title{background:transparent;border:0;box-shadow:none;padding:0}.ex-title h1{font-size:30px;font-weight:700;line-height:1.25;color:#111827;letter-spacing:normal}.ex-title p{font-size:14px;color:#6b7280}.ex-title span{font-size:12px;font-weight:600}.ex-title button{background:#2563eb;padding:12px 20px;border-radius:12px;box-shadow:none;font-weight:600}.ex-title button:hover{background:#1d4ed8}
.ex-filters{border:1px solid #e5e7eb;border-radius:16px;padding:22px 24px;box-shadow:none}.ex-filters>.mb-3{font-size:16px;font-weight:600;margin-bottom:18px}.ex-filters label,.ex-filters .flex-col.text-xs{font-size:13px;font-weight:500;color:#6b7280}.ex-filters input,.ex-filters select{font-size:14px;border-radius:10px;border-color:#d1d5db}.ex-filters .text-\[10px\]{font-size:12px}.ex-filters button{font-size:14px;border-radius:10px;font-weight:600}
.ex-stats{gap:20px}.ex-stats>div{padding:22px 24px;border-color:#e5e7eb;border-radius:16px;box-shadow:none}.ex-stats>div:first-child{border-color:#93c5fd}.ex-stats .text-sm{font-size:14px;color:#4b5563;font-weight:500}.ex-stats .text-xs{font-size:12px;font-weight:500;letter-spacing:normal}.ex-stats .text-3xl{font-size:28px;font-weight:700;letter-spacing:normal}.ex-stats .text-xl{font-size:22px;font-weight:700}.ex-stats>div:first-child .text-3xl{color:#2563eb}.ex-stats>div:nth-child(2) .text-3xl{color:#7c3aed}.ex-stats>div:nth-child(3) .text-xl{color:#d97706}
.ex-table-panel{border-color:#e5e7eb;border-radius:16px;box-shadow:none}.ex-table-panel table{font-size:14px;line-height:1.5}.ex-table-panel thead{background:#f9fafb;color:#6b7280;border-bottom:1px solid #e5e7eb}.ex-table-panel th{font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.4px;padding:16px 18px}.ex-table-panel td{font-size:14px;padding:18px;border-color:#f3f4f6}.ex-table-panel tbody tr:hover{background:#f9fafb}.ex-table-panel td.font-semibold{font-weight:600}.ex-table-panel td.font-black{font-weight:600}.ex-table-panel td span{font-size:12px;font-weight:500;border-radius:8px;padding:5px 8px}.ex-table-panel td button{width:36px;height:36px;border:1px solid #e5e7eb;border-radius:8px}.ex-table-panel tfoot{background:#f9fafb;border-top:1px solid #e5e7eb}.ex-table-panel tfoot td{font-size:14px;font-weight:600}.ex-table-panel .text-xs{font-size:12px}
.ex-modal{background:#15243a88;backdrop-filter:blur(4px)}.ex-modal>div{border:1px solid #e5e7eb;border-radius:16px;box-shadow:0 30px 100px #182c5040}.ex-modal h2{font-size:22px;font-weight:700}.ex-modal label{font-size:14px;font-weight:500}.ex-modal input,.ex-modal textarea,.ex-modal select{font-size:14px;border-color:#d1d5db;border-radius:10px}.ex-modal button{font-weight:600}.ex-modal .text-xs{font-size:12px}.ex-content button:focus-visible,.ex-modal button:focus-visible{outline:2px solid #5385ef;outline-offset:3px}
@media(max-width:767px){.ex-title h1{font-size:26px}.ex-title p{font-size:13px}.ex-filters{padding:18px 16px}.ex-stats{gap:14px}.ex-stats>div{padding:18px}.ex-table-panel .md\:hidden .text-sm{font-size:14px}.ex-modal h2{font-size:20px}}
`;